import { ofType } from 'redux-observable';
import { mergeMap, map, filter } from 'rxjs/operators';
import { StripeService } from 'services';
import { PlaidService } from 'services';

import { basicEpic, mappingEpic } from 'redux/ducks/helpers';
import { SHOW_SPINNER, HIDE_SPINNER } from 'redux/ducks/LoadingSpinner';
import { error, success } from 'redux/ducks/Flash';
import { update as updateUser } from 'redux/ducks/User';

import * as actions from './index';
import { CREATE_SESSION_SUCCESS, GOOGLE_LOGIN_SUCCESS, MICROSOFT_LOGIN_SUCCESS } from '../Session';

// Epics
// ========================================================

export const retrieveSourceEpic = basicEpic(actions.RETRIEVE_SOURCE, (payload, state) =>
  new StripeService(state.session, payload).get()
);

export const mapUpdatePaymentToRetrieveSourceEpics = (action$, store) =>
  action$.pipe(
    ofType(CREATE_SESSION_SUCCESS, GOOGLE_LOGIN_SUCCESS, MICROSOFT_LOGIN_SUCCESS, actions.SUBMIT_SUCCESS),
    map(action => ({
      type: actions.RETRIEVE_SOURCE,
      payload: store.getState().session.currentUser.id,
    }))
  );

export function savePaymentEpic(action$, store) {
  return action$.pipe(
    ofType(actions.SUBMIT),
    mergeMap(action => {
      if (action.payload.token) {
        return new StripeService(store.getState().session, action.payload.id)
          .update(action.payload.token)
          .then(result => ({ type: actions.SUBMIT_SUCCESS, payload: result }))
          .catch(error => ({ type: actions.SUBMIT_ERROR, payload: error }));
      } else {
        return new PlaidService(store.getState().session, action.payload.id)
          .update(action.payload.plaid)
          .then(result => ({ type: actions.SUBMIT_SUCCESS, payload: result }))
          .catch(error => ({ type: actions.SUBMIT_ERROR, payload: error }));
      }
    })
  );
}

// Re-records the mandate once the bank account becomes something the backend can
// attach consent to. It is already recorded when the operator agrees (see
// Checking#onMandateAccepted), but a brand-new operator has no bank account on
// the Stripe customer and no Plaid link at that moment, so the backend declines.
// Both actions here fix that:
//   SUBMIT_SUCCESS                  — the Stripe source is now attached.
//   CREATE_PLAID_ACCESS_TOKEN_SUCCESS — micro-deposit verification has started,
//     so plaid_access_token is set and EventOperator#ach_mandate_applicable? can
//     see them; without this their consent would go unrecorded until a later
//     visit, because that branch never submits.
// For everyone else this refreshes the record to the moment the account became
// chargeable. State is read on the action itself, before anything can reset
// stripeTerms. The backend guard still decides, so neither action can create a
// record for an operator who isn't on (or heading to) ACH.
//
// Sends only the id and the flag: serializing the whole user PATCHes the entire
// profile, and phone_no is not round-trip safe (deserialize strips non-digits,
// serialize re-prefixes a 1).
export const acceptAchMandateEpic = (action$, store) =>
  action$.pipe(
    ofType(actions.SUBMIT_SUCCESS, actions.CREATE_PLAID_ACCESS_TOKEN_SUCCESS),
    filter(() => {
      const { object, stripeTerms } = store.getState().payment;
      return object === 'bank_account' && !!stripeTerms;
    }),
    mergeMap((action) => {
      const record = updateUser({
        id: store.getState().session.currentUser.id,
        acceptAchMandate: true,
      });

      if (action.type !== actions.SUBMIT_SUCCESS) return [record];

      // Consent is per linking attempt. Once the account is saved, linking a
      // different one must present the authorization again instead of silently
      // inheriting this agreement — otherwise a mandate gets recorded for an
      // account whose terms were never shown.
      //
      // Cleared from here, emitted alongside the record above, rather than in
      // submitReducer or a second epic: reducers run before epics, so resetting
      // it in submitReducer would hide it from the filter above, and a separate
      // epic could clear it first depending on subscription order.
      return [record, actions.updateField('stripeTerms', false)];
    })
  );

export const createPlaidPublicTokenEpic = (action$, store) => {
  return action$.pipe(
    ofType(actions.RETRIEVE_SOURCE_SUCCESS, actions.CREATE_PLAID_ACCESS_TOKEN_SUCCESS),
    map(action => ({
      type: actions.GET_PLAID_PUBLIC_TOKEN,
      payload: action.payload,
    }))
  );
};

export const getPlaidPublicTokenEpic = basicEpic(actions.GET_PLAID_PUBLIC_TOKEN, (payload, state) =>
  new PlaidService(state.session, state.session.currentUser.id).get()
);

export const createPlaidAccessTokenEpic = basicEpic(actions.CREATE_PLAID_ACCESS_TOKEN, (payload, state) =>
  new PlaidService(state.session, payload.id).createAccessToken(payload.token)
);

const successMessages = {
  [actions.CREATE_PLAID_ACCESS_TOKEN_SUCCESS]:
    'Micro-deposits can take up to 1 business day. You will receive an email when it has been deposited. Please return to this page to verify.',
  [actions.SUBMIT_SUCCESS]: 'Your Payment Information Was Successfully Updated.',
};

const errorMessages = {
  [actions.CREATE_PLAID_ACCESS_TOKEN_ERROR]: 'There has been an error.',
};

export const successMessagesEpic = mappingEpic(
  [actions.CREATE_PLAID_ACCESS_TOKEN_SUCCESS, actions.SUBMIT_SUCCESS],
  ({ type }) => success(successMessages[type])
);

export const errorMessagesEpic = mappingEpic([actions.CREATE_PLAID_ACCESS_TOKEN_ERROR], ({ type, payload }) =>
  error([errorMessages[type], payload.message].join(': '))
);

export const getPlaidLinkEpic = basicEpic(actions.GET_PLAID_LINK, (payload, state) =>
  new PlaidService(state.session, payload.id).getPlaidLink()
);

export const showSpinnerEpic = mappingEpic(
  [
    actions.CREATE_PLAID_ACCESS_TOKEN,
    actions.RETRIEVE_SOURCE,
    actions.SUBMIT,
    actions.CREATE_PLAID_ACCESS_TOKEN,
    actions.GET_PLAID_PUBLIC_TOKEN,
  ],
  SHOW_SPINNER
);

export const hideSpinnerEpic = mappingEpic(
  [
    actions.RETRIEVE_SOURCE_SUCCESS,
    actions.RETRIEVE_SOURCE_ERROR,
    actions.SUBMIT_SUCCESS,
    actions.SUBMIT_ERROR,
    actions.CREATE_PLAID_ACCESS_TOKEN_ERROR,
    actions.CREATE_PLAID_ACCESS_TOKEN_SUCCESS,
    actions.GET_PLAID_PUBLIC_TOKEN_SUCCESS,
    actions.GET_PLAID_PUBLIC_TOKEN_ERROR,
  ],
  HIDE_SPINNER
);
